import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { MarketOrderType } from '../../../../app/services/api/orderManagementClient';
import OrderChannelItemRow from './OrderChannelItemRow';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../../components/shared/Pill';
import { EmptyRows } from '../../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import LoadingWheel from '../../../../components/ui/LoadingWheel';

type Props = {
  list: MarketOrderType[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const OrderChannelsList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('orderTypes');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('procurementOrderChannels', 'Order Channels')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('procurementCreateNew', 'Create New Order Channel')}
        onAddIsCreating={false}
        withPaginationShown={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('procurementOrderChannels', 'Order Channels')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('procurementOrderChannels', 'Order Channels')}`} />}
        </Box>
      </Box>

      {isLoading ? (
        <LoadingWheel />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('procurementOrderChannelId', 'Order Channel ID')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('procurementOrderChannelName', 'Order Channel Name')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('procurementMarketName', 'Market Name')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('procurementInstanceName', 'Instance Name')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('procurementOrderChannelCutoffTime', 'Order Channel Cutoff Time')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((orderType) => (
                    <OrderChannelItemRow
                      key={orderType.companyOrderTypeId}
                      orderType={orderType}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('procurementNoOrderChannelsFound', 'No order channels found')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default OrderChannelsList;
